import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, TextField, Grid,
  InputAdornment, Card, CardContent, Button
} from '@mui/material';
import {
  Search as SearchIcon, Payment as PaymentIcon,
  AccountBalanceWallet as WalletIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../services/api';
import FinanceCompanyPageHeader from '../../components/Finance/FinanceCompanyPageHeader';
import { useFinanceCompanyReload } from '../../hooks/useFinanceCompanyReload';

const METHOD_COLORS = {
  bank_transfer: 'primary', check: 'secondary', cheque: 'secondary',
  cash: 'success', other: 'default'
};

export default function CustomerPayments() {
  const [data, setData]       = useState({ payments: [], totalAmount: 0, count: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [filters, setFilters] = useState({ fromDate: '', toDate: '', search: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate)   params.toDate   = filters.toDate;
      if (filters.search)   params.search   = filters.search;
      const res = await api.get('/finance/customer-payments', { params });
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load customer payments');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useFinanceCompanyReload(load, { skipInitial: true });

  const fmt = (n) => `PKR ${Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK') : '—';

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <FinanceCompanyPageHeader title="Customer Payments" icon={PaymentIcon}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} size="small">Refresh</Button>
      </FinanceCompanyPageHeader>

      {/* Summary Card */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <WalletIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Total Receipts</Typography>
              </Box>
              <Typography variant="h5" fontWeight={800} color="success.dark">{fmt(data.totalAmount)}</Typography>
              <Typography variant="caption" color="text.secondary">{data.count} payment{data.count !== 1 ? 's' : ''}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Search customer / invoice / reference"
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="From Date" type="date"
              value={filters.fromDate}
              onChange={(e) => setFilters(f => ({ ...f, fromDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="To Date" type="date"
              value={filters.toDate}
              onChange={(e) => setFilters(f => ({ ...f, toDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" onClick={load} size="small">Apply</Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                {['Payment Date', 'Invoice #', 'Customer', 'Method', 'Reference', 'Amount'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                    No customer payments found for selected criteria
                  </TableCell>
                </TableRow>
              ) : (
                data.payments.map((p, i) => (
                  <TableRow key={p._id || i} hover sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' } }}>
                    <TableCell>{fmtDate(p.paymentDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary.main">{p.invoiceNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{p.customerName}</Typography>
                      {p.customerEmail && <Typography variant="caption" color="text.secondary">{p.customerEmail}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(p.paymentMethod || 'other').replace('_', ' ')}
                        size="small"
                        color={METHOD_COLORS[p.paymentMethod] || 'default'}
                      />
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{p.reference || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="success.dark">
                        {fmt(p.amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {data.payments.length > 0 && (
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell colSpan={5} sx={{ fontWeight: 700 }}>Total Receipts ({data.count} payments)</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: 'success.dark' }}>{fmt(data.totalAmount)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
